import { useState, useEffect } from 'react';
import { Stack, Group, Center, Loader, Alert, TextInput, Textarea, Button } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useSupplierDocRepository } from '@ayocore/erp/accounting/client/rpc';
import type { AccountingDocument } from '@ayocore/erp/accounting/schemas/document.schema';
import logger from '@ayocore/platform/common/helpers/logger';

export interface EditSupplierDocFormProps extends Record<string, unknown> {
  documentId: number;
  onSaved?: () => void;
}

/**
 * Форма редактирования документа поставщика.
 * Поля: Номер, Дата, Сумма с НДС, НДС, Комментарий.
 *
 * Пользователь вводит total_amount (с НДС) и tax_amount. Поле amount
 * вычисляется по платформенному инварианту: amount = total_amount − tax_amount.
 */
export function EditSupplierDocForm({ documentId, onSaved }: EditSupplierDocFormProps) {
  const supplierDocRepository = useSupplierDocRepository();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [number, setNumber] = useState('');
  const [operationDate, setOperationDate] = useState<Date | null>(null);
  const [totalAmount, setTotalAmount] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const doc = (await supplierDocRepository.findById(documentId)) as AccountingDocument | null;
        if (!doc) {
          setError('Документ не найден');
          return;
        }
        setNumber(doc.number || '');
        setOperationDate(doc.operation_date ? new Date(doc.operation_date) : null);
        setTotalAmount(doc.total_amount != null ? String(doc.total_amount) : '');
        setTaxAmount(doc.tax_amount != null ? String(doc.tax_amount) : '');
        setDescription(doc.description || '');
      } catch (err) {
        logger.error('Ошибка загрузки документа:', err);
        setError(err instanceof Error ? err.message : 'Не удалось загрузить документ');
      } finally {
        setLoading(false);
      }
    })();
  }, [documentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const total = totalAmount ? parseFloat(totalAmount) : 0;
      const tax = taxAmount ? parseFloat(taxAmount) : 0;
      await supplierDocRepository.update(documentId, {
        number: number.trim() || null,
        operation_date: operationDate ?? undefined,
        amount: total - tax,
        tax_amount: tax,
        total_amount: total,
        description: description.trim() || null,
      });
      onSaved?.();
    } catch (err) {
      logger.error('Ошибка сохранения документа:', err);
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Center py='xl'>
        <Loader size='sm' />
      </Center>
    );

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap='sm'>
        {error && (
          <Alert color='red' withCloseButton onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextInput
          label='Номер'
          size='xs'
          value={number}
          onChange={e => setNumber(e.currentTarget.value)}
        />

        <DatePickerInput
          label='Дата'
          size='xs'
          value={operationDate}
          onChange={val =>
            setOperationDate(val ? (typeof val === 'string' ? new Date(val) : val) : null)
          }
          valueFormat='DD.MM.YYYY'
          clearable
        />

        <Group grow>
          <TextInput
            label='Сумма с НДС'
            size='xs'
            type='number'
            step='0.01'
            value={totalAmount}
            onChange={e => setTotalAmount(e.currentTarget.value)}
          />
          <TextInput
            label='НДС'
            size='xs'
            type='number'
            step='0.01'
            value={taxAmount}
            onChange={e => setTaxAmount(e.currentTarget.value)}
          />
        </Group>

        <Textarea
          label='Комментарий'
          size='xs'
          autosize
          minRows={2}
          value={description}
          onChange={e => setDescription(e.currentTarget.value)}
        />

        <Group justify='flex-end' pt='sm'>
          <Button type='submit' size='xs' loading={saving}>
            Сохранить
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
